// ---------------------------------------------------------------------------
// Sidebar — HotelStack (SOURCE OF TRUTH)
// ---------------------------------------------------------------------------
// SYNC NOTE: This component is the canonical version of the Sidebar shared
// between HotelStack and BookMyRoom. Both apps use identical layout, accordion,
// mobile drawer, and collapsed-tooltip logic. The ONLY differences are:
//   1. Default `brandName` / `brandLabel` prop values (app-specific branding)
//   2. HotelStack imports `Megaphone` icon + has `megaphone` key in iconMap
//      (BookMyRoom does not use the megaphone icon in its sidebar menus)
//   3. HotelStack imports `Music2` for the platform stock-music catalogue
//      (/admin/music). BookMyRoom is the guest booking site and has no music
//      menu, so it does not need the icon.
// When modifying sidebar behaviour, apply the same change to both apps.
// See: apps/bookmyroom.io/shared/layout/Sidebar.tsx
// ---------------------------------------------------------------------------
'use client';

import { createElement, useState, useRef, useEffect, useCallback, useTransition, type RefCallback, type PointerEvent as ReactPointerEvent } from 'react';
import { useFocusTrap } from '@/core/hooks/useFocusTrap';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';
import { useNavigationSkeleton } from '@/shared/feedback';
import type { MenuItem } from '@/core/types/common.types';

// ---------------------------------------------------------------------------
// Converts a label like "Front Desk" to a nav translation key like "frontDesk"
// ---------------------------------------------------------------------------
function labelToNavKey(label: string): string {
  const words = label.replace(/[&]+/g, '').trim().split(/[\s\-]+/).filter(Boolean);
  if (words.length === 0) return '';
  return (
    words[0].toLowerCase() +
    words.slice(1).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('')
  );
}
import {
  LayoutDashboard,
  CalendarDays,
  CalendarCheck,
  BedDouble,
  DoorOpen,
  ConciergeBell,
  Users,
  UtensilsCrossed,
  BarChart3,
  CreditCard,
  Moon,
  UsersRound,
  Building2,
  Building,
  TrendingUp,
  Monitor,
  Wrench,
  UserCog,
  MessageSquare,
  FileText,
  ShieldCheck,
  Heart,
  Bell,
  History,
  Plug,
  Settings,
  Link2,
  ArrowRight,
  Hotel,
  Banknote,
  Flag,
  Palette,
  Gift,
  ClipboardList,
  Globe,
  KeyRound,
  Home,
  Receipt,
  Tag,
  Coffee,
  Layers,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  PieChart,
  ScrollText,
  Puzzle,
  Award,
  ScanLine,
  UserCircle,
  Share2,
  LifeBuoy,
  DatabaseBackup,
  Megaphone,
  Music2,
  Brain,
  ShieldAlert,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Icon lookup — maps string identifiers to actual lucide-react components
// ---------------------------------------------------------------------------
const iconMap: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  LayoutDashboard,
  calendar: CalendarDays,
  CalendarDays,
  CalendarCheck,
  bed: BedDouble,
  BedDouble,
  door: DoorOpen,
  DoorOpen,
  concierge: ConciergeBell,
  ConciergeBell,
  users: Users,
  Users,
  utensils: UtensilsCrossed,
  UtensilsCrossed,
  chart: BarChart3,
  BarChart3,
  'credit-card': CreditCard,
  CreditCard,
  moon: Moon,
  Moon,
  people: UsersRound,
  UsersRound,
  building: Building2,
  Building2,
  Building,
  trending: TrendingUp,
  TrendingUp,
  monitor: Monitor,
  Monitor,
  wrench: Wrench,
  Wrench,
  staff: UserCog,
  UserCog,
  message: MessageSquare,
  MessageSquare,
  file: FileText,
  FileText,
  shield: ShieldCheck,
  ShieldCheck,
  heart: Heart,
  Heart,
  bell: Bell,
  Bell,
  history: History,
  History,
  plug: Plug,
  Plug,
  settings: Settings,
  Settings,
  link: Link2,
  Link2,
  'arrow-right': ArrowRight,
  ArrowRight,
  hotel: Hotel,
  Hotel,
  money: Banknote,
  Banknote,
  flag: Flag,
  Flag,
  palette: Palette,
  Palette,
  gift: Gift,
  Gift,
  clipboard: ClipboardList,
  ClipboardList,
  globe: Globe,
  Globe,
  key: KeyRound,
  KeyRound,
  home: Home,
  Home,
  receipt: Receipt,
  Receipt,
  tag: Tag,
  Tag,
  coffee: Coffee,
  Coffee,
  layers: Layers,
  Layers,
  PieChart,
  ScrollText,
  Puzzle,
  Award,
  ScanLine,
  UserCircle,
  Share2,
  LifeBuoy,
  DatabaseBackup,
  Megaphone,
  megaphone: Megaphone,
  Music2,
  // Used by menu entries that rendered the dashboard icon instead (AI Revenue,
  // AI Anomalies, Payments): resolveIcon falls back silently.
  Brain,
  ShieldAlert,
  Wallet,
};

function resolveIcon(name?: string): LucideIcon {
  if (!name) return LayoutDashboard;
  return iconMap[name] ?? LayoutDashboard;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface SidebarProps {
  items: MenuItem[];
  brandName?: string;
  brandLabel?: string;
  collapsed?: boolean;
  onToggle?: () => void;
  /** Mobile drawer open state */
  mobileOpen?: boolean;
  /** Close mobile drawer */
  onMobileClose?: () => void;
}

// ---------------------------------------------------------------------------
// Sub-item expandable section
// ---------------------------------------------------------------------------
function SubItems({
  children,
  isOpen,
  collapsed,
  t,
  onNavigate,
}: {
  children: MenuItem[];
  isOpen: boolean;
  collapsed: boolean;
  t: (key: string) => string;
  /** Closes the mobile drawer. A submenu tap left it open over the new page. */
  onNavigate?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.scrollHeight);
    }
  }, [children, isOpen]);

  if (collapsed) return null;

  return (
    <div
      className="overflow-hidden transition-all duration-300 ease-in-out"
      style={{ maxHeight: isOpen ? height : 0, opacity: isOpen ? 1 : 0 }}
      // A collapsed submenu was hidden only VISUALLY: `maxHeight: 0` and
      // `opacity: 0` clip the paint, but the child links keep their own
      // geometry and stay hittable. Measured in the browser with every
      // accordion closed, `a[href="/front-desk"]` reported
      // display:block / visibility:visible / opacity:1 at y=197 — two pixels
      // from "Kiosk Mode" at y=195 — and `elementsFromPoint` at the centre of
      // the visible Kiosk row returned the HIDDEN link on top.
      //
      // So a click on a visible item could land on an invisible one: either
      // navigating somewhere unexpected, or hitting a link whose own target is
      // the page already shown, which `beginNavigation` correctly ignores —
      // and nothing at all happens. That is the reported symptom, and no
      // amount of skeleton coverage fixes it, because the click never reaches
      // the item that was aimed at.
      //
      // `inert` removes the subtree from hit-testing, focus and the
      // accessibility tree in one step, so a collapsed submenu is also no
      // longer a set of invisible tab stops.
      inert={!isOpen}
      aria-hidden={!isOpen}
    >
      <div ref={ref} className="ms-[22px] ps-4 border-s border-hms-border/60 mt-1 space-y-0.5">
        {children.map((child) => (
          <SubItemLink key={child.href} item={child} t={t} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  );
}

function hrefToId(href: string): string {
  return href.replace(/^\//, '').replace(/\//g, '-') || 'root';
}

/**
 * A submenu entry — "Invoices" under Billing, "Calendar" under Reservations.
 *
 * This was a bare `<Link>`: no transition, no skeleton, no `aria-busy`. So
 * every child item in every portal acknowledged a click with NOTHING until the
 * new page rendered. Measured against a 1.2s-delayed payload on
 * /admin/billing/invoices: the click landed and navigated correctly, and for
 * 3.6 seconds the screen did not change at all.
 *
 * That is over half the menu — the top-level rows had `useTransition` and
 * `beginNavigation` wired in; their children never did — and it is the same
 * "did my click register?" symptom, from the same cause.
 *
 * Kept deliberately identical to the parent row's handler so the two cannot
 * drift: acknowledge synchronously, paint the target's skeleton, then push.
 */
function SubItemLink({ item, t, onNavigate }: { item: MenuItem; t: (key: string) => string; onNavigate?: () => void }) {
  const pathname = usePathname();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const { beginNavigation } = useNavigationSkeleton();
  const isActive = pathname === item.href;
  const navKey = `nav.${labelToNavKey(item.label)}`;
  const translated = t(navKey);
  const displayLabel = translated !== navKey ? translated : item.label;

  return (
    <Link
      id={`sidebar-link-${hrefToId(item.href)}`}
      href={item.href}
      onClick={(e) => {
        // Modified clicks (new tab, middle-click) keep their native behaviour.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        // Closes the mobile drawer, exactly as the top-level row does.
        onNavigate?.();
        beginNavigation(item.href);
        startTransition(() => router.push(item.href));
      }}
      aria-busy={isPending || undefined}
      className={cn(
        'block px-3 py-[7px] rounded-lg text-[12.5px] font-medium transition-all duration-200',
        isActive || isPending
          ? 'text-hms-gold bg-hms-gold/5'
          : 'text-hms-textDim hover:text-hms-textSub hover:bg-white/[0.03]'
      )}
    >
      {displayLabel}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Tooltip for collapsed mode
// ---------------------------------------------------------------------------
function CollapsedTooltip({
  label,
  visible,
  dir,
}: {
  label: string;
  visible: boolean;
  dir: 'ltr' | 'rtl';
}) {
  return (
    <div
      className={cn(
        'absolute top-1/2 -translate-y-1/2 z-50',
        'px-3 py-1.5 rounded-lg bg-hms-card border border-hms-border shadow-xl shadow-black/40',
        'text-[12px] font-medium text-hms-text whitespace-nowrap',
        'pointer-events-none transition-all duration-200',
        visible ? 'opacity-100' : 'opacity-0 invisible',
        dir === 'ltr' ? 'left-full ml-3' : 'right-full mr-3',
        visible
          ? 'translate-x-0'
          : dir === 'ltr'
            ? '-translate-x-1'
            : 'translate-x-1'
      )}
    >
      {label}
    </div>
  );
}

function NavIcon({ name, active }: { name?: string; active: boolean }) {
  return createElement(resolveIcon(name), {
    className: cn(
      'flex-shrink-0 transition-colors duration-200',
      active ? 'text-hms-gold' : 'text-hms-textDim group-hover:text-hms-textSub'
    ),
    size: 18,
    strokeWidth: 1.8,
  });
}

// ---------------------------------------------------------------------------
// Single navigation item
// ---------------------------------------------------------------------------
function NavItem({
  item,
  collapsed,
  onNavigate,
  accordionOpen,
  onAccordionToggle,
  t,
  dir,
}: {
  item: MenuItem;
  collapsed: boolean;
  onNavigate?: () => void;
  accordionOpen?: boolean;
  onAccordionToggle?: () => void;
  t: (key: string) => string;
  dir: 'ltr' | 'rtl';
}) {
  const pathname = usePathname();
  const router = useRouter();
  const [tooltipVisible, setTooltipVisible] = useState(false);

  /*
   * Acknowledge the click IMMEDIATELY, before the network is involved.
   *
   * `isActive` is derived from `usePathname()`, which only updates once
   * navigation COMPLETES. So between the click and the server responding,
   * nothing on screen changed at all: the clicked item did not highlight, no
   * skeleton appeared, and the previous page stayed fully rendered.
   *
   * Measured with the server unreachable: 2.02s of zero visual change before
   * the request even failed. That is indistinguishable from a dead button, so
   * users click again — and again — believing the first click was missed.
   *
   * `useTransition` gives a pending flag that flips synchronously on click and
   * is pure client state, so it works with NO network at all. This is the half
   * of the fix that does not depend on how any page fetches its data.
   */
  const [isPending, startTransition] = useTransition();
  const { beginNavigation } = useNavigationSkeleton();

  const navKey = `nav.${labelToNavKey(item.label)}`;
  const translated = t(navKey);
  const displayLabel = translated !== navKey ? translated : item.label;

  const hasChildren = !!(item.children && item.children.length > 0);
  const isActive = hasChildren
    ? pathname === item.href || pathname.startsWith(item.href + '/')
    : pathname === item.href;
  const childActive = hasChildren && item.children
    ? item.children.some((c) => pathname === c.href || pathname.startsWith(c.href + '/'))
    : false;
  const isOpen = hasChildren ? (accordionOpen ?? false) : false;
  const sharedClasses = cn(
    'group relative flex items-center gap-3 rounded-lg text-[13px] font-medium transition-all duration-200 w-full',
    collapsed ? 'justify-center px-0 py-2.5 mx-auto w-11 h-11' : 'px-3 py-2.5',
    (isActive || childActive || isPending)
      ? 'bg-hms-gold/10 text-hms-gold'
      : 'text-hms-textSub hover:text-hms-text hover:bg-white/[0.05]'
  );

  const iconEl = <NavIcon name={item.icon} active={isActive || childActive} />;

  const activeIndicator = (isActive || childActive || isPending) && (
    <span className="absolute start-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-hms-gold" />
  );

  return (
    <div className="relative">
      {hasChildren ? (
        <button
          id={`sidebar-item-${hrefToId(item.href)}`}
          type="button"
          aria-expanded={isOpen}
          onClick={onAccordionToggle}
          onMouseEnter={() => collapsed && setTooltipVisible(true)}
          onMouseLeave={() => setTooltipVisible(false)}
          className={sharedClasses}
        >
          {activeIndicator}
          {iconEl}
          {!collapsed && (
            <>
              <span className="truncate">{displayLabel}</span>
              {item.badge != null && (
                <span className="ms-auto text-[10px] font-bold bg-hms-gold/20 text-hms-gold px-2 py-0.5 rounded-full leading-none">
                  {item.badge}
                </span>
              )}
               <ChevronDown
                className={cn(
                  'ms-auto flex-shrink-0 transition-transform duration-300 text-hms-textDim',
                  isOpen && 'rotate-180 text-hms-gold/60',
                  item.badge != null && 'ms-2'
                )}
                size={14}
                strokeWidth={2}
              />
            </>
          )}
        </button>
      ) : (
        <Link
          id={`sidebar-item-${hrefToId(item.href)}`}
          href={item.href}
          onClick={(e) => {
            // Let modified clicks (new tab, middle-click) behave natively.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            onNavigate?.();
            // Paint the TARGET route's skeleton on this same tick.
            //
            // `loading.tsx` cannot do this: it is a server boundary, so it
            // renders only once Next has begun fetching the route. Measured
            // against a 1.5s-delayed payload the server skeleton appeared
            // 3,403ms after the click, and offline it never appeared. The
            // destination is known here, synchronously, so the skeleton is
            // rendered from the click rather than from the response.
            beginNavigation(item.href);
            // Flips isPending synchronously — the row highlights on this tick,
            // with no network involved.
            startTransition(() => router.push(item.href));
          }}
          aria-busy={isPending || undefined}
          onMouseEnter={() => collapsed && setTooltipVisible(true)}
          onMouseLeave={() => setTooltipVisible(false)}
          className={sharedClasses}
          data-active={isActive ? 'true' : 'false'}
        >
          {activeIndicator}
          {iconEl}
          {!collapsed && (
            <>
              <span className="truncate">{displayLabel}</span>
              {item.badge != null && (
                <span className="ms-auto text-[10px] font-bold bg-hms-gold/20 text-hms-gold px-2 py-0.5 rounded-full leading-none">
                  {item.badge}
                </span>
              )}
            </>
          )}
        </Link>
      )}

      {/* Collapsed tooltip */}
      {collapsed && <CollapsedTooltip label={displayLabel} visible={tooltipVisible} dir={dir} />}

      {/* Sub-items */}
      {hasChildren && item.children && (
        <SubItems isOpen={isOpen} collapsed={collapsed} t={t} onNavigate={onNavigate}>
          {item.children}
        </SubItems>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Navigation content (shared between desktop sidebar and mobile drawer)
// ---------------------------------------------------------------------------
function SidebarNav({
  items,
  isCollapsed,
  onNavigate,
  t,
  dir,
}: {
  items: MenuItem[];
  isCollapsed: boolean;
  onNavigate?: () => void;
  t: (key: string) => string;
  dir: 'ltr' | 'rtl';
}) {
  const pathname = usePathname();

  // Accordion state: only one expandable menu open at a time.
  // Initialize with the parent menu that contains the active route.
  const [openMenuHref, setOpenMenuHref] = useState<string | null>(() => {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        const isParentActive = pathname === item.href || pathname.startsWith(item.href + '/');
        const isChildActive = item.children.some(
          (c) => pathname === c.href || pathname.startsWith(c.href + '/')
        );
        if (isParentActive || isChildActive) return item.href;
      }
    }
    return null;
  });

  const handleAccordionToggle = useCallback((href: string) => {
    setOpenMenuHref((prev) => (prev === href ? null : href));
  }, []);

  const renderItem = (item: MenuItem) => (
    <NavItem
      key={item.href}
      item={item}
      collapsed={isCollapsed}
      onNavigate={onNavigate}
      accordionOpen={openMenuHref === item.href}
      onAccordionToggle={() => handleAccordionToggle(item.href)}
      t={t}
      dir={dir}
    />
  );

  const hasSections = items.some((item) => item.section);

  if (!hasSections) {
    return (
      <div className="space-y-0.5">
        {items.map(renderItem)}
      </div>
    );
  }

  const sectionOrder: string[] = [];
  const grouped: Record<string, MenuItem[]> = {};
  for (const it of items) {
    const key = it.section || 'Other';
    if (!grouped[key]) {
      grouped[key] = [];
      sectionOrder.push(key);
    }
    grouped[key].push(it);
  }

  return (
    <>
      {sectionOrder.map((section) => {
        const secKey = `sections.${labelToNavKey(section)}`;
        const translatedSection = t(secKey);
        const displaySection = translatedSection !== secKey ? translatedSection : section;
        return (
        <div key={section} className="mb-1">
          {!isCollapsed && (
            <p
              id={`sidebar-section-${section.toLowerCase().replace(/[\s&]+/g, '-')}`}
              className="text-[9px] font-bold text-hms-textDim uppercase tracking-[0.15em] px-3 pt-4 pb-1.5"
            >
              {displaySection}
            </p>
          )}
          {isCollapsed && <div className="h-px bg-hms-border/40 mx-1 my-2" />}
          <div className="space-y-0.5">
            {grouped[section].map(renderItem)}
          </div>
        </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------
export function Sidebar({
  items,
  brandName = 'Apex Residency',
  brandLabel = 'Hotel Management',
  collapsed: controlledCollapsed,
  onToggle,
  mobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  const { t, dir } = useTranslation();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isCollapsed = controlledCollapsed ?? internalCollapsed;
  const focusTrapRef = useFocusTrap(mobileOpen);

  const handleToggle = useCallback(() => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalCollapsed((prev) => !prev);
    }
  }, [onToggle]);

  const handleMobileNavigate = useCallback(() => {
    onMobileClose?.();
  }, [onMobileClose]);

  // Swipe-to-dismiss for mobile drawer (left sidebar → swipe left to close)
  const pointerStartX = useRef<number | null>(null);
  const pointerStartY = useRef<number | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);

  const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    pointerStartX.current = e.clientX;
    pointerStartY.current = e.clientY;
  }, []);

  const handlePointerUp = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (pointerStartX.current === null || pointerStartY.current === null) return;
    const deltaX = e.clientX - pointerStartX.current;
    const deltaY = Math.abs(e.clientY - pointerStartY.current);
    // Swipe toward the drawer edge to dismiss: left (LTR) or right (RTL)
    const isHorizontal = deltaY < Math.abs(deltaX);
    const isSwipeClose = dir === 'ltr' ? deltaX < -100 : deltaX > 100;
    if (isSwipeClose && isHorizontal) {
      onMobileClose?.();
    }
    pointerStartX.current = null;
    pointerStartY.current = null;
  }, [onMobileClose, dir]);

  // Scrollbar nav classes
  const scrollbarClasses = cn(
    '[&::-webkit-scrollbar]:w-[3px]',
    '[&::-webkit-scrollbar-track]:bg-transparent',
    '[&::-webkit-scrollbar-thumb]:bg-hms-border/50',
    '[&::-webkit-scrollbar-thumb]:rounded-full',
    'hover:[&::-webkit-scrollbar-thumb]:bg-hms-border'
  );

  // Brand icon (shared styling, no ID duplication)
  const brandIcon = (
    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-hms-gold to-hms-goldLt flex items-center justify-center flex-shrink-0 shadow-lg shadow-hms-gold/10">
      <Building2 className="text-hms-bg" size={18} strokeWidth={2.2} />
    </div>
  );

  // -------------------------------------------------------------------------
  // Mobile Drawer (only rendered when open — avoids z-50 conflicts)
  // -------------------------------------------------------------------------
  const mobileDrawer = mobileOpen ? (
    <>
      {/* Backdrop */}
      <div
        id="sidebar-mobile-backdrop"
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm lg:hidden"
        onClick={onMobileClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
        <aside
          ref={focusTrapRef as React.RefObject<HTMLElement>}
          id="sidebar-mobile-drawer"
          role="dialog"
          aria-modal="true"
          aria-label={t('common.navigationMenu')}
          className="fixed inset-y-0 start-0 z-[60] w-[280px] max-w-[85vw] flex flex-col bg-hms-sidebar lg:hidden touch-pan-y"
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
        >
        <div className="flex items-center border-b border-hms-border/60 flex-shrink-0 px-5 h-16 gap-3">
          {brandIcon}
          <div className="overflow-hidden flex-1">
            <p dir="auto" className="text-[15px] font-semibold text-hms-text tracking-tight truncate leading-tight">
              {brandName}
            </p>
            <p className="text-[10.5px] text-hms-textDim font-medium tracking-wide uppercase leading-tight mt-0.5">
              {brandLabel}
            </p>
          </div>
          <button
            id="sidebar-mobile-close"
            onClick={onMobileClose}
            className="ms-auto flex items-center justify-center w-8 h-8 rounded-lg text-hms-textDim hover:text-hms-text hover:bg-white/[0.05] transition-colors"
            aria-label={t('common.closeSidebar')}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        <nav id="sidebar-mobile-nav" className={cn('flex-1 overflow-y-auto py-3 px-3', scrollbarClasses)}>
          <SidebarNav items={items} isCollapsed={false} onNavigate={handleMobileNavigate} t={t} dir={dir} />
        </nav>
      </aside>
    </>
  ) : null;

  // -------------------------------------------------------------------------
  // Desktop Sidebar (standard sticky sidebar)
  // -------------------------------------------------------------------------
  const desktopSidebar = (
    <aside
      id="sidebar-aside"
      className={cn(
        'h-screen sticky top-0 flex-col bg-hms-sidebar transition-all duration-300 ease-in-out select-none flex-shrink-0',
        'hidden lg:flex',
        isCollapsed ? 'w-[72px]' : 'w-[260px]'
      )}
    >
      <div
        className={cn(
          'flex items-center border-b border-hms-border/60 flex-shrink-0',
          isCollapsed ? 'justify-center h-16' : 'px-5 h-16 gap-3'
        )}
      >
        <div id="sidebar-brand-icon" className="w-9 h-9 rounded-xl bg-gradient-to-br from-hms-gold to-hms-goldLt flex items-center justify-center flex-shrink-0 shadow-lg shadow-hms-gold/10">
          <Building2 className="text-hms-bg" size={18} strokeWidth={2.2} />
        </div>
        {!isCollapsed && (
          <div id="sidebar-brand-text" className="overflow-hidden flex-1">
            <p id="sidebar-brand-name" dir="auto" className="text-[15px] font-semibold text-hms-text tracking-tight truncate leading-tight">
              {brandName}
            </p>
            <p id="sidebar-brand-label" className="text-[10.5px] text-hms-textDim font-medium tracking-wide uppercase leading-tight mt-0.5">
              {brandLabel}
            </p>
          </div>
        )}
      </div>

      <nav
        id="sidebar-nav"
        className={cn('flex-1 overflow-y-auto py-3', isCollapsed ? 'px-2' : 'px-3', scrollbarClasses)}
      >
        <SidebarNav items={items} isCollapsed={isCollapsed} t={t} dir={dir} />
      </nav>

      {/* Collapse Toggle */}
      <div className="flex-shrink-0 p-3 border-t border-hms-border/60">
        <button
          id="sidebar-collapse-btn"
          onClick={handleToggle}
          className={cn(
            'flex items-center justify-center gap-2 rounded-lg text-hms-textDim',
            'hover:text-hms-textSub hover:bg-white/[0.05] transition-all duration-200',
            isCollapsed ? 'w-11 h-10 mx-auto' : 'w-full px-3 py-2.5'
          )}
          aria-label={isCollapsed ? t('common.expandSidebar') : t('common.collapseSidebar')}
        >
          {isCollapsed ? (
            dir === 'rtl' ? <ChevronLeft size={16} strokeWidth={2} /> : <ChevronRight size={16} strokeWidth={2} />
          ) : (
            <>
              {dir === 'rtl' ? <ChevronRight size={16} strokeWidth={2} /> : <ChevronLeft size={16} strokeWidth={2} />}
              <span className="text-[12px] font-medium">{t('topbar.collapse')}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );

  return (
    <>
      {desktopSidebar}
      {mobileDrawer}
    </>
  );
}
